import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  useBulkCreateItems,
  useItemKinds,
  type BulkCreateItemResult,
} from '@/features/inventory/hooks';
import type { PreparedItemRow } from '@/features/inventory/spreadsheet';

// ExcelJS is heavy (~hundreds of KB), so the spreadsheet helpers are
// pulled in on demand inside the handlers below. That keeps the
// library out of the main bundle and only loads it the first time an
// operator actually downloads a template or uploads a file.
const loadSpreadsheet = () => import('@/features/inventory/spreadsheet');

interface ImportItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful import so the page can refresh / toast. */
  onImported?: (result: BulkCreateItemResult) => void;
}

type Step = 'upload' | 'preview' | 'result';

/**
 * Bulk "Add items" flow. Three steps in one modal:
 *   1. upload  — download the Excel template + pick a filled-in file
 *   2. preview — per-row validation against the org's kinds catalog
 *   3. result  — what landed and what didn't, after the insert
 *
 * The template's column contract, the parser, and the preview table all
 * read from `ITEM_IMPORT_COLUMNS` (see `features/inventory/spreadsheet`)
 * so they stay in lockstep.
 */
export function ImportItemsDialog({ open, onOpenChange, onImported }: ImportItemsDialogProps) {
  const { data: kinds } = useItemKinds();
  const bulkCreate = useBulkCreateItems();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rows, setRows] = useState<PreparedItemRow[]>([]);
  const [result, setResult] = useState<BulkCreateItemResult | null>(null);

  const validRows = useMemo(() => rows.filter((r) => r.input && r.errors.length === 0), [rows]);
  const errorRows = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);

  const reset = () => {
    setStep('upload');
    setFileName(null);
    setParsing(false);
    setRows([]);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      // Don't yank the modal out from under an in-flight import.
      if (bulkCreate.isPending) return;
      reset();
    }
    onOpenChange(next);
  };

  const handleDownloadTemplate = async () => {
    setGenerating(true);
    try {
      const { buildItemImportTemplate, downloadBlob } = await loadSpreadsheet();
      const blob = await buildItemImportTemplate(kinds ?? []);
      downloadBlob(blob, 'atlas-items-template.xlsx');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not build the template.');
    } finally {
      setGenerating(false);
    }
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setFileName(file.name);
    try {
      const { parseItemImportFile, prepareItemRows } = await loadSpreadsheet();
      const parsed = await parseItemImportFile(file);
      if (parsed.length === 0) {
        toast.error('No rows found in that file. Add items under the headers and try again.');
        setParsing(false);
        return;
      }
      const prepared = prepareItemRows(parsed, kinds ?? []);
      setRows(prepared);
      setStep('preview');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read that file.');
    } finally {
      setParsing(false);
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const handleImport = async () => {
    const inputs = validRows.map((r) => r.input!).filter(Boolean);
    if (inputs.length === 0) return;
    try {
      const res = await bulkCreate.mutateAsync(inputs);
      setResult(res);
      setStep('result');
      onImported?.(res);
      if (res.created > 0) {
        toast.success(
          res.created === 1 ? 'Imported 1 item.' : `Imported ${res.created} items.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import items from a spreadsheet</DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="mt-2 space-y-4">
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Step 1 — Get the template</p>
              </div>
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                disabled={generating}
                className="shrink-0"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download template
              </Button>
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
              className={cn(
                'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-background px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-secondary/30',
                parsing && 'pointer-events-none opacity-70',
              )}
            >
              {parsing ? (
                <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-7 w-7 text-muted-foreground" />
              )}
              <span className="text-sm font-medium text-foreground">
                {parsing ? 'Reading file…' : 'Step 2 — Upload your filled-in spreadsheet'}
              </span>
              <span className="text-xs text-muted-foreground">
                {fileName ?? 'Click to choose a .xlsx file'}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={onFileInputChange}
            />
          </div>
        )}

        {step === 'preview' && (
          <div className="mt-2 space-y-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/50 px-2.5 py-1">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                {rows.length} {rows.length === 1 ? 'row' : 'rows'}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-bright-moss/20 px-2.5 py-1 text-bright-moss-dark">
                <CheckCircle2 className="h-4 w-4" />
                {validRows.length} ready
              </span>
              {errorRows.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-fresh-salmon/15 px-2.5 py-1 text-fresh-salmon-dark">
                  <AlertTriangle className="h-4 w-4" />
                  {errorRows.length} with errors
                </span>
              )}
            </div>

            <div className="max-h-[45vh] overflow-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-secondary text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const hasError = r.errors.length > 0;
                    return (
                      <tr key={r.rowNumber} className={cn(hasError && 'bg-fresh-salmon/5')}>
                        <td className="px-3 py-2 align-top tabular-nums text-muted-foreground">
                          {r.rowNumber}
                        </td>
                        <td className="px-3 py-2 align-top font-mono text-xs">
                          {r.sku || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.name || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {hasError ? (
                            <span className="inline-flex items-start gap-1.5 text-fresh-salmon-dark">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              <span className="text-xs">{r.errors.join(' ')}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-bright-moss-dark">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span className="text-xs">Ready</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="mt-2 space-y-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-bright-moss/20 px-2.5 py-1 text-bright-moss-dark">
                <CheckCircle2 className="h-4 w-4" />
                {result.created} created
              </span>
              {result.failures.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-fresh-salmon/15 px-2.5 py-1 text-fresh-salmon-dark">
                  <AlertTriangle className="h-4 w-4" />
                  {result.failures.length} failed
                </span>
              )}
            </div>

            {result.failures.length > 0 && (
              <div className="max-h-[40vh] overflow-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-secondary text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">SKU</th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {result.failures.map((f, i) => (
                      <tr key={`${f.sku}-${i}`}>
                        <td className="px-3 py-2 align-top font-mono text-xs">{f.sku || '—'}</td>
                        <td className="px-3 py-2 align-top">{f.name || '—'}</td>
                        <td className="px-3 py-2 align-top text-xs text-fresh-salmon-dark">
                          {f.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-6">
          {step === 'upload' && (
            <Button variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
          )}

          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={reset} disabled={bulkCreate.isPending}>
                Choose another file
              </Button>
              <Button onClick={handleImport} disabled={validRows.length === 0 || bulkCreate.isPending}>
                {bulkCreate.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  `Import ${validRows.length} ${validRows.length === 1 ? 'item' : 'items'}`
                )}
              </Button>
            </>
          )}

          {step === 'result' && (
            <>
              <Button variant="ghost" onClick={reset}>
                Import more
              </Button>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
